// Summary: KDS page container for ticket list, actions, and print-preview orchestration.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFileLines,
  faGear,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button, Input, Select } from './utils/primitives';
import { Button as SharedButton } from '../../components/Button';
import { KdsPrintTicket } from './components/print/KdsPrintTicket';
import { KdsOrderCard } from './components/cards/KdsOrderCard';
import { useKdsController } from './hooks/useKdsController';
import { kdsI18n } from './i18n/kds-i18n';
import { type KdsNotificationSound } from './audio/kds-notification-sound';
import { type KdsTab, type KdsOrderSort, type KdsOrderTypeFilter } from './utils/kds-page';
import classes from './styles/kds-page.module.scss';

export type KdsPageProps = { tenantId: string };

// Summary: Full KDS (Kitchen Display System) screen as a thin layout/wiring layer.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
// Architectural summary: Full KDS (Kitchen Display System) screen - thin layout/wiring layer.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
// All state, effects, and handlers live in useKdsController (Rule 32). Card rendering lives in
// KdsOrderCard. This file owns only layout composition: top bar, tab bar, settings panel,
// print-preview modal, and the order grid.
export const KdsPage: React.FC<KdsPageProps> = ({ tenantId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(undefined, { i18n: kdsI18n });

  const {
    employeeSession,
    isLoading,
    loadError,
    activeTab,
    setActiveTab,
    tabCards,
    sortedTabCards,
    overviewGroups,
    kdsCategories,
    hasUncategorized,
    categoryFilter,
    setCategoryFilter,
    isDark,
    favorites,
    searchQuery,
    setSearchQuery,
    isOverviewOpen,
    setIsOverviewOpen,
    isSettingsOpen,
    setIsSettingsOpen,
    isSoundUnlockPromptVisible,
    language,
    handleLanguageChange,
    zoom,
    handleZoomChange,
    orderSort,
    handleOrderSortChange,
    orderTypeFilter,
    handleOrderTypeFilterChange,
    notificationSound,
    handleNotificationSoundChange,
    kitchenPrinterId,
    handleKitchenPrinterChange,
    showDoneTab,
    handleShowDoneTabChange,
    mergeOrders,
    handleMergeOrdersChange,
    settingsPrinters,
    settingsPrintersLoading,
    printPreview,
    isQueueingPrint,
    printPreviewError,
    gridRef,
    printReceiptPaperRef,
    printPreviewModalRef,
    printTicketWrapperRef,
    printPreviewActionsRef,
    tryPrimeNotificationSound,
    handleToggleDark,
    handleToggleFavorite,
    handleStatusTap,
    handleMarkAllReady,
    handleMarkAllCooking,
    handleMarkAllDone,
    handleMarkAllReadyFromDone,
    handleHide,
    handleOpenPrintPreview,
    handleClosePrintPreview,
    handleConfirmPrint,
  } = useKdsController(tenantId);
  const isPhone = useMediaQuery('(max-width: 30rem)');
  const isRtl = language === 'he';

  // Origin-aware back: use the caller-supplied return path, else fall back by session presence.
  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    void navigate(from ?? (employeeSession ? `/t/${tenantId}/pos` : `/t/${tenantId}/backoffice`));
  };
  const tabLabels: Record<KdsTab, string> = {
    COOKING: t('tab.cooking'),
    READY: t('tab.ready'),
    DONE: t('tab.done'),
  };
  const orderSortLabels: Record<KdsOrderSort, string> = {
    duration: t('settings.orderSortOptions.duration'),
    product_count: t('settings.orderSortOptions.product_count'),
    table: t('settings.orderSortOptions.table'),
  };
  const orderTypeFilterLabels: Record<KdsOrderTypeFilter, string> = {
    all: t('settings.orderTypeFilterOptions.all'),
    dine_in: t('settings.orderTypeFilterOptions.dine_in'),
    take_away: t('settings.orderTypeFilterOptions.take_away'),
  };

  const pageClass = [classes.page, isDark ? classes.dark : ''].filter(Boolean).join(' ');
  const printTicketLabels = {
    newBadge: tabLabels[activeTab],
    orderNumber: t('print.orderNumberLabel'),
    time: t('print.timeLabel'),
    waiter: t('print.waiterLabel'),
    diners: t('print.dinersLabel'),
    placeholder: t('print.placeholderValue'),
    table: t('print.tableLabel'),
    diningType: t('print.diningTypePlaceholder'),
  };

  return (
    <div className={pageClass} data-testid="kds-page">

        {/* Summary: Top bar controls for navigation, theme, and settings.
          Details: docs/function-library/web/contexts-pages.md#pages-kdspage */}
      <header className={classes.topBar}>
        <div className={classes.topBarLeft}>
          <Button
            className={classes.iconBtn}
            onClick={handleBack}
            aria-label={t('topBar.backToPos')}
            data-testid="kds-back"
          >
            <FontAwesomeIcon icon={faArrowLeft as IconProp} />
          </Button>
          <span className={classes.tenantName}>{tenantId.toUpperCase()}</span>
        </div>

        <div className={classes.topBarRight}>
          <Button
            className={classes.iconBtn}
            onClick={handleToggleDark}
            aria-label={isDark ? t('topBar.switchToLightMode') : t('topBar.switchToDarkMode')}
            data-testid="kds-theme-toggle"
          >
            <span
              className={[classes.toggle, isDark ? classes.toggleOn : ''].filter(Boolean).join(' ')}
              aria-hidden="true"
            />
          </Button>

          <Button className={classes.iconBtn} aria-label={t('topBar.settings')} onClick={() => setIsSettingsOpen(true)} data-testid="kds-settings-open">
            <FontAwesomeIcon icon={faGear as IconProp} />
          </Button>
        </div>
      </header>

        {/* Summary: Status tabs and search controls for KDS queue filtering.
          Details: docs/function-library/web/contexts-pages.md#pages-kdspage */}
      <nav className={classes.tabBar}>
        <div className={classes.tabBarLeft}>
          <Button
            className={classes.iconBtn}
            aria-label={t('tabBar.menu')}
            onClick={() => setIsOverviewOpen((prev) => !prev)}
            data-testid="kds-overview-toggle"
          >
            <FontAwesomeIcon icon={faFileLines as IconProp} />
          </Button>
        </div>

        <div className={classes.tabList}>
          {(['COOKING', 'READY', 'DONE'] as KdsTab[])
            .filter((tab) => tab !== 'DONE' || showDoneTab)
            .map((tab) => (
            <Button
              key={tab}
              className={[classes.tabBtn, activeTab === tab ? classes.tabBtnActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab.toLowerCase()}`}
            >
              {tabLabels[tab]}
              <span className={[classes.tabBadge, classes[`badge${tab}`]].join(' ')}>
                {tabCards[tab].length}
              </span>
            </Button>
          ))}
        </div>

        <div className={classes.tabBarRight}>
          <div className={classes.searchBar}>
            <FontAwesomeIcon icon={faMagnifyingGlass as IconProp} className={classes.searchIcon} />
            <Input
              className={classes.searchInput}
              type="text"
              placeholder={isPhone ? t('tabBar.search') : t('tabBar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t('tabBar.searchOrders')}
              data-testid="kds-search"
            />
          </div>
        </div>
      </nav>

      {kdsCategories.length > 0 && (
        <div className={classes.categoryChips} dir={isRtl ? 'rtl' : 'ltr'} data-testid="kds-category-chips">
          <Button
            className={[classes.categoryChip, categoryFilter === 'all' ? classes.categoryChipActive : ''].filter(Boolean).join(' ')}
            onClick={() => setCategoryFilter('all')}
            data-testid="kds-category-all"
          >
            {t('overview.allCategories')}
          </Button>
          {kdsCategories.map((category) => (
            <Button
              key={category.id}
              className={[classes.categoryChip, categoryFilter === category.id ? classes.categoryChipActive : ''].filter(Boolean).join(' ')}
              onClick={() => setCategoryFilter(category.id)}
              data-testid={`kds-category-${category.id}`}
            >
              {category.name}
            </Button>
          ))}
          {hasUncategorized && (
            <Button
              className={[classes.categoryChip, categoryFilter === 'uncategorized' ? classes.categoryChipActive : ''].filter(Boolean).join(' ')}
              onClick={() => setCategoryFilter('uncategorized')}
              data-testid="kds-category-uncategorized"
            >
              {t('overview.uncategorized')}
            </Button>
          )}
        </div>
      )}

      {isSoundUnlockPromptVisible && (
        <section className={classes.soundUnlockBanner} dir={isRtl ? 'rtl' : 'ltr'} data-testid="kds-sound-unlock-banner">
          <span className={classes.soundUnlockText}>{t('alerts.soundLocked')}</span>
          <Button
            className={[classes.soundUnlockBtn, 'btn-bordered'].join(' ')}
            onClick={() => tryPrimeNotificationSound()}
            aria-label={t('alerts.enableSound')}
            data-testid="kds-enable-sound"
          >
            {t('alerts.enableSound')}
          </Button>
        </section>
      )}

      <div className={classes.contentArea}>
        {isOverviewOpen && (
          <aside className={classes.overviewPanel} dir={isRtl ? 'rtl' : 'ltr'} data-testid="kds-overview-panel">
            <header className={classes.overviewHeader}>
              <span className={classes.overviewTitle}>{t('overview.title')}</span>
              <Button
                className={classes.iconBtn}
                onClick={() => setIsOverviewOpen(false)}
                aria-label={t('overview.close')}
                data-testid="kds-overview-close"
              >
                ✕
              </Button>
            </header>

            {overviewGroups.length === 0 ? null : (
              <div className={classes.overviewBody}>
                {overviewGroups.map((group) => (
                  <React.Fragment key={group.categoryId ?? 'uncategorized'}>
                    <h3 className={classes.overviewCategoryTitle}>
                      {group.categoryName ?? t('overview.uncategorized')}
                    </h3>
                    <ul className={classes.overviewList}>
                      {group.lines.map((line) => (
                        <li key={line.productName} className={classes.overviewListItem}>
                          <span className={[classes.overviewItemQty, classes[`badge${activeTab}`]].join(' ')}>
                            {line.quantity}
                          </span>
                          <span className={classes.overviewItemName}>{line.productName}</span>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ))}
              </div>
            )}
          </aside>
        )}

        {/* Summary: Main KDS card grid with loading/empty/error states.
          Details: docs/function-library/web/contexts-pages.md#pages-kdspage */}
        <main ref={gridRef} className={classes.grid} data-testid="kds-grid">
        {isLoading && (
          <p className={classes.stateMsg} data-testid="kds-loading">
            {t('state.loadingOrders')}
          </p>
        )}
        {loadError && (
          <p className={classes.stateMsg} data-testid="kds-error">
            {loadError}
          </p>
        )}
        {!isLoading && !loadError && sortedTabCards.length === 0 && (
          <p className={classes.stateMsg} data-testid="kds-empty">
            {t('state.noActiveOrders')}
          </p>
        )}
        {!isLoading &&
          sortedTabCards.map((card) => (
              <KdsOrderCard
                key={`${activeTab}-${card.cardId}`}
                order={card.displayOrder}
                sourceOrder={card.sourceOrder}
                orderTab={activeTab}
                isPartial={card.isPartial}
                isFavorite={favorites.has(card.sourceOrder.id)}
                onToggleFavorite={handleToggleFavorite}
                onStatusTap={handleStatusTap}
                onMarkAllReady={handleMarkAllReady}
                onMarkAllCooking={handleMarkAllCooking}
                onMarkAllDone={handleMarkAllDone}
                onMarkAllReadyFromDone={handleMarkAllReadyFromDone}
                onHide={handleHide}
                onPrint={handleOpenPrintPreview}
              />
            ))}
        </main>
      </div>

      {printPreview && (
        <>
          <div
            className={classes.printPreviewOverlay}
            onClick={handleClosePrintPreview}
            aria-hidden="true"
          />
          {/* Off-screen - no transforms, clean DOM for html2canvas + font capture */}
          <div aria-hidden="true" className={classes.printCaptureOffscreen}>
            <KdsPrintTicket
              printerName={printPreview.printerName}
              tableNumber={printPreview.tableNumber}
              isTakeAway={printPreview.isTableless}
              orderNumber={printPreview.orderNumber}
              dinersCount={printPreview.dinersCount}
              printTime={printPreview.printTime}
              waiterName={printPreview.waiterName}
              locale={language === 'he' ? 'he-IL' : 'en-US'}
              labels={{ ...printTicketLabels, diningType: printPreview.isTakeAway ? t('print.diningTypeTakeAway') : t('print.diningTypeDineIn') }}
              orderlines={printPreview.orderlines}
              receiptPaperRef={printReceiptPaperRef}
            />
          </div>

          <section ref={printPreviewModalRef} className={classes.printPreviewModal} dir={isRtl ? 'rtl' : 'ltr'} data-testid="kds-print-preview">
            <div ref={printTicketWrapperRef} className={classes.printTicketWrapper}>
              <KdsPrintTicket
                printerName={printPreview.printerName}
                tableNumber={printPreview.tableNumber}
                isTakeAway={printPreview.isTableless}
                orderNumber={printPreview.orderNumber}
                dinersCount={printPreview.dinersCount}
                printTime={printPreview.printTime}
                waiterName={printPreview.waiterName}
                locale={language === 'he' ? 'he-IL' : 'en-US'}
                labels={{ ...printTicketLabels, diningType: printPreview.isTakeAway ? t('print.diningTypeTakeAway') : t('print.diningTypeDineIn') }}
                orderlines={printPreview.orderlines}
                isFlat
              />
            </div>

            <div ref={printPreviewActionsRef} className={classes.printPreviewActions}>
              <SharedButton
                variant="cancel"
                className={classes.printPreviewCancelBtn}
                onClick={handleClosePrintPreview}
                ariaLabel={t('print.cancelPrint')}
                disabled={isQueueingPrint}
                dataTestId="kds-print-cancel"
              >
                {t('print.cancel')}
              </SharedButton>
              <SharedButton
                variant="confirm"
                className={classes.printPreviewConfirmBtn}
                onClick={() => void handleConfirmPrint()}
                ariaLabel={t('print.confirmPrint')}
                disabled={isQueueingPrint}
                dataTestId="kds-print-confirm"
              >
                {isQueueingPrint ? t('print.sending') : t('print.print')}
              </SharedButton>
            </div>
            {printPreviewError && <p className={classes.printPreviewError}>{printPreviewError}</p>}
          </section>
        </>
      )}

      {/* Settings panel */}
      {isSettingsOpen && (
        <>
          <div
            className={classes.settingsOverlay}
            onClick={() => setIsSettingsOpen(false)}
            aria-hidden="true"
          />
          <aside className={classes.settingsPanel} dir={isRtl ? 'rtl' : 'ltr'} data-testid="kds-settings-panel">
            <header className={classes.settingsPanelHeader}>
              <span className={classes.settingsPanelTitle}>{t('settings.title')}</span>
              <Button
                className={classes.iconBtn}
                onClick={() => setIsSettingsOpen(false)}
                aria-label={t('settings.close')}
                data-testid="kds-settings-close"
              >
                ✕
              </Button>
            </header>

            <div className={classes.settingsPanelBody}>
              {/* Zoom */}
              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>
                  {t('settings.ticketZoom')}: {Math.round(zoom * 100)}%
                </label>
                <Input
                  className={classes.settingsSlider}
                  type="range"
                  min={0.5}
                  max={2}
                  step={0.1}
                  value={zoom}
                  aria-label={t('settings.ticketZoom')}
                  onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                  data-testid="kds-zoom"
                />
              </div>

              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>{t('settings.language')}</label>
                <Select
                  className={classes.settingsSelect}
                  value={language}
                  aria-label={t('settings.language')}
                  onChange={(e) => handleLanguageChange(e.target.value === 'en' ? 'en' : 'he')}
                  data-testid="kds-language"
                >
                  <option value="he">{t('settings.languageOptionHebrew')}</option>
                  <option value="en">{t('settings.languageOptionEnglish')}</option>
                </Select>
              </div>

              {/* Order of orders */}
              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>{t('settings.orderSort')}</label>
                <Select
                  className={classes.settingsSelect}
                  value={orderSort}
                  aria-label={t('settings.orderSort')}
                  onChange={(e) => handleOrderSortChange(e.target.value as KdsOrderSort)}
                  data-testid="kds-order-sort"
                >
                  {(Object.entries(orderSortLabels) as [KdsOrderSort, string][]).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </div>

              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>{t('settings.orderTypeFilter')}</label>
                <Select
                  className={classes.settingsSelect}
                  value={orderTypeFilter}
                  aria-label={t('settings.orderTypeFilter')}
                  onChange={(e) => handleOrderTypeFilterChange(e.target.value as KdsOrderTypeFilter)}
                  data-testid="kds-order-type-filter"
                >
                  {(Object.entries(orderTypeFilterLabels) as [KdsOrderTypeFilter, string][]).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </div>

              {/* Kitchen printer */}
              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>{t('settings.kitchenPrinter')}</label>
                {settingsPrintersLoading ? (
                  <span className={classes.settingsMuted}>{t('settings.loadingPrinters')}</span>
                ) : (
                  <Select
                    className={classes.settingsSelect}
                    value={kitchenPrinterId ?? ''}
                    aria-label={t('settings.kitchenPrinter')}
                    onChange={(e) => handleKitchenPrinterChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                    data-testid="kds-kitchen-printer"
                  >
                    <option value="">{t('settings.noPrinter')}</option>
                    {settingsPrinters.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                )}
              </div>

              {/* Notification sound */}
              <div className={classes.settingsSection}>
                <label className={classes.settingsLabel}>{t('settings.notificationSound')}</label>
                <Select
                  className={classes.settingsSelect}
                  value={notificationSound}
                  aria-label={t('settings.notificationSound')}
                  onChange={(e) => handleNotificationSoundChange(e.target.value as KdsNotificationSound)}
                  data-testid="kds-notification-sound"
                >
                  <option value="none">{t('settings.notificationSoundNone')}</option>
                  <option value="test_message">{t('settings.notificationSoundTest')}</option>
                </Select>
                <span className={classes.settingsMuted}>
                  {t('settings.notificationMutedLine1')}
                </span>
                <span className={classes.settingsMuted}>
                  {t('settings.notificationMutedLine2')}
                </span>
              </div>

              {/* Merge orders */}
              <div className={classes.settingsSection}>
                <div className={classes.settingsCheckRow}>
                  <span className={classes.settingsLabel}>{t('settings.mergeOrders')}</span>
                  <Button
                    className={classes.iconBtn}
                    onClick={() => handleMergeOrdersChange(!mergeOrders)}
                    aria-label={t('settings.mergeOrders')}
                    data-testid="kds-merge-toggle"
                  >
                    <span
                      className={[classes.toggle, mergeOrders ? classes.toggleOn : ''].filter(Boolean).join(' ')}
                      aria-hidden="true"
                    />
                  </Button>
                </div>
              </div>

              {/* Show done tab */}
              <div className={classes.settingsSection}>
                <div className={classes.settingsCheckRow}>
                  <span className={classes.settingsLabel}>{t('settings.showDoneTab')}</span>
                  <Button
                    className={classes.iconBtn}
                    onClick={() => handleShowDoneTabChange(!showDoneTab)}
                    aria-label={t('settings.showDoneTab')}
                    data-testid="kds-show-done-toggle"
                  >
                    <span
                      className={[classes.toggle, showDoneTab ? classes.toggleOn : ''].filter(Boolean).join(' ')}
                      aria-hidden="true"
                    />
                  </Button>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};
